import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Inbox, RefreshCw, type LucideIcon } from 'lucide-react';

/**
 * Primitives de estado compartidos del shell (Fase 1).
 * Mismo lenguaje visual para loading / empty / error / confirmación,
 * para que mobile y desktop no reinventen estos bloques.
 */

export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-ui-text-subtle">
      <span className="w-5 h-5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center text-center gap-2 py-10 px-6">
      <span className="w-11 h-11 flex items-center justify-center rounded-full bg-ui-accent/10 text-ui-accent">
        <Icon size={20} strokeWidth={1.75} />
      </span>
      <p className="text-sm font-medium text-ui-text mt-1">{title}</p>
      {description && <p className="text-sm text-ui-text-subtle max-w-sm">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'No se pudo cargar',
  description = 'Vuelve a intentarlo en un momento.',
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center text-center gap-2 py-10 px-6">
      <span className="w-11 h-11 flex items-center justify-center rounded-full bg-ui-danger/10 text-ui-danger">
        <AlertTriangle size={20} strokeWidth={1.75} />
      </span>
      <p className="text-sm font-medium text-ui-text mt-1">{title}</p>
      <p className="text-sm text-ui-text-subtle max-w-sm">{description}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-ui-border px-3 py-1.5 text-sm text-ui-text hover:bg-ui-text/[0.06] transition-colors"
        >
          <RefreshCw size={14} />
          Reintentar
        </button>
      )}
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'accent',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'accent' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmClass =
    tone === 'danger'
      ? 'bg-ui-danger text-ui-text-inverse hover:opacity-90'
      : 'bg-ui-accent text-ui-text-inverse hover:bg-ui-accent-hover';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onCancel}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm bg-ui-surface border border-ui-border rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <p className="text-base font-semibold text-ui-text">{title}</p>
              {description && <p className="mt-1.5 text-sm text-ui-text-subtle">{description}</p>}
            </div>
            <div className="flex gap-2 p-4 pt-0">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 rounded-lg border border-ui-border px-3 py-2 text-sm text-ui-text hover:bg-ui-text/[0.06] transition-colors"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${confirmClass}`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
